import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Camera, Check, X, RefreshCw, Mail, Loader2, ChevronRight, AlertTriangle, FileText } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const ANGLES = [
  { key: "front",     label: "Front of vehicle",       hint: "Stand 2-3m back · capture the bonnet, grille, both headlights and front bumper in frame." },
  { key: "rear",      label: "Rear of vehicle",        hint: "Stand 2-3m back · capture both tail lights, rear bumper and number plate." },
  { key: "near_side", label: "Near-side (driver UK)",  hint: "Capture the whole drivers-side flank — front wing, both doors, rear quarter." },
  { key: "off_side",  label: "Off-side (passenger)",   hint: "Capture the whole passenger-side flank — front wing, both doors, rear quarter." },
  { key: "wheel_fl",  label: "Front-left wheel",       hint: "Crouch low · close-up of the alloy face plus the tyre sidewall." },
  { key: "wheel_fr",  label: "Front-right wheel",      hint: "Crouch low · close-up of the alloy face plus the tyre sidewall." },
  { key: "wheel_rl",  label: "Rear-left wheel",        hint: "Crouch low · close-up of the alloy face plus the tyre sidewall." },
  { key: "wheel_rr",  label: "Rear-right wheel",       hint: "Crouch low · close-up of the alloy face plus the tyre sidewall." },
];


export default function CheckInPhotosModal({ jobId, open, onClose }) {
  const [photos, setPhotos] = useState([]); // [{angle, captured_at}]
  const [busy, setBusy] = useState(false);
  const [sendingPdf, setSendingPdf] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const fileRef = useRef(null);
  const [pdfSentAt, setPdfSentAt] = useState(null);

  const refresh = useCallback(async () => {
    if (!jobId) return;
    try {
      const r = await api.get(`/jobs/${jobId}/checkin-photos`);
      setPhotos(r.data.photos || []);
      setPdfSentAt(r.data.pdf_sent_at || null);
      // Auto-advance to the first missing angle
      const captured = new Set((r.data.photos || []).map(p => p.angle));
      const nextMissing = ANGLES.findIndex(a => !captured.has(a.key));
      if (nextMissing >= 0) setActiveIdx(nextMissing);
    } catch { /* ignore */ }
  }, [jobId]);

  useEffect(() => {
    if (open) refresh();
  }, [open, refresh]);

  const captured = useMemo(() => new Set(photos.map(p => p.angle)), [photos]);
  const completeCount = photos.length;
  const total = ANGLES.length;
  const allDone = completeCount === total;
  const activeAngle = ANGLES[activeIdx];

  const onFileChosen = async (file) => {
    if (!file || !activeAngle) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("angle", activeAngle.key);
      fd.append("file", file);
      await api.post(`/jobs/${jobId}/checkin-photos`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000,
      });
      toast.success(`${activeAngle.label} captured`);
      await refresh();
      // Move on to next missing angle automatically
      const nextIdx = ANGLES.findIndex((a, i) => i > activeIdx && !captured.has(a.key));
      if (nextIdx >= 0) setActiveIdx(nextIdx);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save the photo");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeAngle = async (angle) => {
    setBusy(true);
    try {
      await api.delete(`/jobs/${jobId}/checkin-photos/${angle}`);
      await refresh();
    } catch (e) {
      toast.error("Couldn't delete the photo");
    } finally { setBusy(false); }
  };

  const sendPdf = async () => {
    setSendingPdf(true);
    try {
      const r = await api.post(`/jobs/${jobId}/checkin-pdf`);
      const sent = r.data?.email?.ok;
      if (sent) toast.success("Condition report emailed to customer");
      else toast.success("Condition report generated · email skipped (no customer email)");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't generate the report");
    } finally { setSendingPdf(false); }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[80] grid place-items-center bg-black/70 backdrop-blur-sm p-4"
         onClick={() => !busy && !sendingPdf && onClose()}
         data-testid="checkin-photos-modal">
      <div onClick={(e) => e.stopPropagation()}
           className="bg-card rounded-xl border border-border shadow-2xl w-full max-w-3xl max-h-[94vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-card border-b border-border p-5 flex items-center justify-between gap-3 z-10">
          <div className="min-w-0">
            <div className="label-overline text-primary">Vehicle condition · check-in</div>
            <h3 className="font-display font-bold text-lg mt-1">Walkaround photos</h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              {completeCount} of {total} captured · timestamped + watermarked automatically
            </p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" disabled={busy || sendingPdf}
                  data-testid="checkin-modal-close" className="text-muted-foreground hover:text-foreground p-1">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {/* Progress bar */}
          <div className="h-2 rounded-full bg-muted/40 overflow-hidden" aria-hidden>
            <div className="h-full bg-primary transition-all" style={{ width: `${(completeCount / total) * 100}%` }} />
          </div>

          {/* Current angle prompt */}
          {!allDone && activeAngle && (
            <div className="rounded-lg border border-primary/30 bg-primary/[0.04] p-5" data-testid="checkin-active-angle">
              <div className="label-overline text-primary">Next photo · {activeIdx + 1} of {total}</div>
              <div className="font-display font-bold text-2xl mt-2">{activeAngle.label}</div>
              <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{activeAngle.hint}</p>
              <input ref={fileRef} type="file" accept="image/*" capture="environment"
                     className="hidden" data-testid="checkin-file-input"
                     onChange={(e) => onFileChosen(e.target.files?.[0])} />
              <div className="flex flex-wrap gap-2 mt-4">
                <button type="button" onClick={() => fileRef.current?.click()} disabled={busy}
                        className="btn-primary" data-testid="checkin-capture-btn">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
                  {busy ? "Saving…" : "Take photo"}
                </button>
                {activeIdx < total - 1 && (
                  <button type="button" onClick={() => setActiveIdx((i) => Math.min(total - 1, i + 1))}
                          disabled={busy} className="btn-outline" data-testid="checkin-skip-btn">
                    Skip <ChevronRight className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          )}

          {allDone && (
            <div className="rounded-lg border border-success/40 bg-success/[0.06] p-5 flex items-start gap-3" data-testid="checkin-complete">
              <Check className="w-6 h-6 text-success shrink-0 mt-0.5" />
              <div>
                <div className="font-display font-bold text-base">All eight angles captured</div>
                <p className="text-sm text-muted-foreground mt-1">
                  Generate the condition report and we'll email it to the customer so you both have a shared record.
                </p>
                <div className="flex flex-wrap gap-2 mt-3">
                  <button type="button" onClick={sendPdf} disabled={sendingPdf}
                          className="btn-primary" data-testid="checkin-send-pdf-btn">
                    {sendingPdf ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                    {sendingPdf ? "Sending…" : (pdfSentAt ? "Re-send report" : "Email condition report")}
                  </button>
                  <a href={`/api/jobs/${jobId}/checkin-pdf/preview`} target="_blank" rel="noreferrer"
                     className="btn-outline" data-testid="checkin-preview-pdf-btn">
                    <FileText className="w-4 h-4" /> Preview PDF
                  </a>
                </div>
                {pdfSentAt && (
                  <div className="text-xs text-success mt-2">
                    Last sent {new Date(pdfSentAt).toLocaleString("en-GB")}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Grid — captured + pending */}
          <div>
            <div className="text-[11px] uppercase tracking-wider font-bold text-muted-foreground mb-2">
              All angles
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="checkin-angle-grid">
              {ANGLES.map((a, i) => {
                const done = captured.has(a.key);
                const isActive = i === activeIdx;
                return (
                  <button key={a.key} type="button"
                          onClick={() => setActiveIdx(i)}
                          className={`relative rounded-lg border p-3 text-left transition-all ${
                            isActive ? "border-primary bg-primary/[0.08]" :
                            done ? "border-success/40 bg-success/[0.05]" :
                            "border-border hover:border-muted-foreground"
                          }`}
                          data-testid={`checkin-angle-tile-${a.key}`}>
                    <div className="flex items-center justify-between">
                      <span className="font-mono-plex text-[10px] text-muted-foreground">{String(i + 1).padStart(2, "0")}</span>
                      {done ? (
                        <Check className="w-4 h-4 text-success" />
                      ) : (
                        <Camera className="w-4 h-4 text-muted-foreground" />
                      )}
                    </div>
                    <div className="font-semibold text-xs mt-2 leading-tight">{a.label}</div>
                    {done && (
                      <button type="button" onClick={(e) => { e.stopPropagation(); removeAngle(a.key); }}
                              className="mt-2 inline-flex items-center gap-1 text-[10px] text-muted-foreground hover:text-destructive"
                              data-testid={`checkin-redo-${a.key}`}>
                        <RefreshCw className="w-3 h-3" /> Re-take
                      </button>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Audit note */}
          <div className="rounded-md border border-border bg-muted/20 p-3 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />
            <div className="text-xs text-muted-foreground leading-relaxed">
              Every photo is stamped with the registration, your name and the exact timestamp before it's stored.
              These photos sit on the job permanently as a defensible record of the vehicle's condition at check-in.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
